import * as _ from 'lodash-es';
import { Delta, Op, Parchment } from 'quill/core';
import type { Bounds, Range } from 'quill/core';
import { tableHandler } from 'quill/modules/tableEmbed';
import React, { createRef } from 'react';
import { selectedClassName } from '../../utils/editor/selectNode';
import { getAssetTokenFromBlot } from '../AssetTokenContext';
import type { ClipboardHooks } from '../Clipboard';
import Quill from '../QuillEditor';
import { getRangeSaver } from '../RangeSaver';
import type { TableAPI } from '../table/TableEmbed';
import Table from '../table/TableEmbed';
import { CELL_IDENTITY_ATTRIBUTE } from '../table/constants';
import convertFromNode from '../table/convertFromNode';
import getRangeSelectionIndexes from '../table/getRangeSelectionIndexes';
import type {
  TableChange,
  TableChangeCell,
  TableChangeColumn,
  TableChangeRow,
  TableDocument,
  TableDocumentCell,
} from '../table/types';
import type {
  ColumnSelection,
  RowSelection,
  Selection,
  TableSelection,
} from '../table/useTableSelection';
import { SelectionType } from '../table/useTableSelection';
import { parseCellIdentity, stringifyCellIdentity } from '../table/utils';
import type { BlockEmbedMovedInOptions } from '../types';
import deltaToHTML from '../utils/deltaToHTML';
import deltaToText from '../utils/deltaToText';
import { findQuillByNode } from '../utils/findByNode';
import findClosestScrollingContainer from '../utils/findClosestScrollingContainer';
import generateDeltaRandomId from '../utils/generateDeltaRandomId';
import moveCaretIntoLineWithKeyboard from '../utils/moveCaretIntoLineWithKeyboard';
import ReactBlockEmbed from './ReactBlockEmbed';
import Span from './Span';
import type CellManager from './utils/CellManager';
import translateBoundary from './utils/translateBoundary';

type RangeSelection = TableSelection | RowSelection | ColumnSelection;

const getCellIdentityAtXOffset = (
  preferredXOffset: number,
  tr: HTMLTableRowElement,
  table: HTMLTableElement,
) => {
  let xOffset = preferredXOffset;

  const restrictXOffset = (container: HTMLElement) => {
    const rect = container.getBoundingClientRect();
    xOffset = Math.min(Math.max(rect.left, xOffset), rect.right);
  };

  restrictXOffset(table);
  const scrollingContainer = findClosestScrollingContainer(table);
  if (scrollingContainer) {
    restrictXOffset(scrollingContainer);
  }

  const tds = [...tr.querySelectorAll('td')];
  return (
    tds
      .find((td) => {
        const rect = td.getBoundingClientRect();
        return rect.right >= xOffset && rect.left <= xOffset;
      })
      ?.getAttribute(CELL_IDENTITY_ATTRIBUTE) ?? null
  );
};

class TableEmbed extends ReactBlockEmbed implements CellManager {
  static setValue() {}

  static value() {
    return true;
  }

  static isEmbedSelected(domNode: HTMLElement) {
    return domNode.classList.contains(selectedClassName);
  }

  tableRef = createRef<TableAPI>();

  selectedObserver = new MutationObserver(() => {
    const isSelected = TableEmbed.isEmbedSelected(this.domNode);

    // @ts-expect-error
    if (this.store.getState().selected !== isSelected) {
      this.setState({ selected: isSelected });
    }
  });

  protected isEmbedded = false;

  constructor(
    scroll: Parchment.ScrollBlot,
    domNode: HTMLElement,
    value: TableDocument,
  ) {
    super(scroll, domNode, value);

    this.setState({
      value,
      span: null,
      selected: TableEmbed.isEmbedSelected(domNode),
    });
  }

  onCaptureCopy(
    e: ClipboardEvent,
    isCut: boolean,
    clipboardHooks: ClipboardHooks,
  ) {
    const { selection, deleteSelection } = this.tableRef.current || {};
    if (selection?.type === SelectionType.Cell) {
      const quill = this.getCellQuill(selection.identity);
      quill?.clipboard.onCaptureCopy(e, isCut);
    } else if (selection) {
      e.preventDefault();

      const { html, text } = this.getClipboardDataForRange(selection);

      e.clipboardData?.setData('text/plain', text);

      const { beforeCut } = clipboardHooks;
      const transformedHtml = isCut && beforeCut ? beforeCut(html) : html;
      e.clipboardData?.setData('text/html', transformedHtml);

      if (isCut) {
        deleteSelection?.();
      }
    }
  }

  handlePaste = (e: ClipboardEvent) => {
    const selection = this.tableRef.current?.selection;
    if (!selection) return;

    const html = e.clipboardData?.getData('text/html');
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const table = doc.querySelector('table');
    if (!table) return;

    table.remove();
    const hasOtherContents = [
      // A naive filter to skip invisible HTML elements ex <style>
      ...doc.querySelectorAll('p, div'),
    ].some((node) => node instanceof Element && !!node.textContent?.trim());
    if (hasOtherContents) {
      return;
    }

    const { cells, rows, columns } = convertFromNode(table);
    if (!cells || !rows || !columns) {
      return;
    }

    const targetFromOffset = { row: 0, column: 0 };

    switch (selection.type) {
      case SelectionType.Cell:
        [targetFromOffset.row, targetFromOffset.column] = parseCellIdentity(
          selection.identity,
        );
        break;
      case SelectionType.Row:
        targetFromOffset.row = selection.rows[0] ?? 0;
        break;
      case SelectionType.Column:
        targetFromOffset.column = selection.columns[0] ?? 0;
        break;
      default:
        break;
    }

    const { value } = this.store.getState() as {
      value: TableDocument;
    };
    const rowCount = value.rows?.length;
    const columnCount = value.columns?.length;

    if (!rowCount || !columnCount) return;

    const cellsChange: Record<string, TableChangeCell> = {};

    _.range(rows.length).forEach((rowIndex) => {
      _.range(columns.length).forEach((columnIndex) => {
        const identity = stringifyCellIdentity(rowIndex, columnIndex);
        const cellContent = cells[identity]?.content;

        const shiftedIdentity = stringifyCellIdentity(
          targetFromOffset.row + rowIndex,
          targetFromOffset.column + columnIndex,
        );

        const existingContent = value.cells?.[shiftedIdentity]?.content;
        if (!cellContent && !existingContent) return;

        const change = new Delta()
          .delete(new Delta(existingContent).length())
          .concat(
            cellContent ? new Delta(cellContent) : new Delta().insert('\n'),
          );
        if (change.ops.length) {
          cellsChange[shiftedIdentity] = { content: change.ops };
          const cellQuill = this.getCellQuill(shiftedIdentity);
          if (existingContent) {
            cellQuill?.updateContents(new Delta(change), Quill.sources.API);
          } else {
            cellQuill?.setContents(new Delta(change), Quill.sources.API);
          }
        }
      });
    });

    const change: TableChange = {};
    if (Object.keys(cellsChange).length) {
      change.cells = cellsChange;
    }

    const createRowsColumns = (at: number, count: number) => {
      const delta = new Delta().retain(at);
      for (let i = 0; i < count; i += 1) {
        delta.insert({ id: generateDeltaRandomId() });
      }
      return delta.ops as (TableChangeRow & TableChangeColumn)[];
    };

    const needRowCount = rows.length + targetFromOffset.row - rowCount;
    if (needRowCount > 0) {
      change.rows = createRowsColumns(rowCount, needRowCount);
    }

    const needColumnCount =
      columns.length + targetFromOffset.column - columnCount;
    if (needColumnCount > 0) {
      change.columns = createRowsColumns(columnCount, needColumnCount);
    }

    this.handleUserChange(change);
    e.preventDefault();
  };

  html() {
    return this.getClipboardDataForRange({ type: SelectionType.Table }).html;
  }

  attach() {
    super.attach();
    this.selectedObserver.observe(this.domNode, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  detach() {
    super.detach();
    this.selectedObserver.disconnect();
  }

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  format(name: string, value: any) {
    if (name === Span.attrName) {
      const attributor = this.scroll.query(name, Parchment.Scope.ATTRIBUTE);
      if (attributor) {
        // @ts-expect-error
        this.attributes.attribute(attributor, value);
      }
      this.setState({ span: value });
    } else {
      super.format(name, value);
    }
  }

  onCaretMovedInWithKeyboard(movedInOptions: BlockEmbedMovedInOptions) {
    switch (movedInOptions.direction) {
      case 'right': {
        this.moveCaretIntoCellWithKeyboard('1:1', movedInOptions);
        break;
      }
      case 'up':
      case 'down': {
        const tableRow = this.domNode.querySelector(
          movedInOptions.direction === 'up'
            ? 'tr:last-child'
            : 'tr:first-child',
        );
        if (tableRow instanceof HTMLTableRowElement) {
          const table = this.domNode.querySelector('table');
          const identity =
            table &&
            getCellIdentityAtXOffset(movedInOptions.xOffset, tableRow, table);
          if (identity) {
            this.moveCaretIntoCellWithKeyboard(identity, movedInOptions);
          }
        }
        break;
      }
      case 'left': {
        const { rows, columns } = this.value()['table-embed'];
        if (rows && columns) {
          this.moveCaretIntoCellWithKeyboard(
            stringifyCellIdentity(rows.length - 1, columns.length - 1),
            movedInOptions,
          );
        }
        break;
      }
      default:
        break;
    }
  }

  private applyInlineFormat(
    format: string,
    value: true | null,
  ): TableChange['cells'] | null {
    const { selection } = this.tableRef.current || {};
    if (!selection || selection.type === SelectionType.Cell) {
      return null;
    }

    const {
      value: { rows, columns, cells },
    } = this.store.getState() as {
      value: TableDocument;
    };
    if (!cells) return null;

    const cellsDeltas: TableChange['cells'] = {};
    const { rowIndexes, columnIndexes } = getRangeSelectionIndexes(selection, {
      rows,
      columns,
    });
    Object.keys(cells).forEach((identity) => {
      const { content } = cells[identity] || {};
      const [row, column] = parseCellIdentity(identity);
      if (
        content &&
        rowIndexes.includes(row) &&
        columnIndexes.includes(column)
      ) {
        const delta = getDeltaForApplyingInlineFormat(
          new Delta(content),
          format,
          value,
        );
        if (delta.length() > 0) {
          cellsDeltas[identity] = { content: delta.ops };
          this.getCellQuill(identity)?.updateContents(delta, Quill.sources.API);
        }
      }
    });
    return cellsDeltas;
  }

  toggleContentFormat(format: string) {
    const values = [true, null] as const;
    values.some((value) => {
      const cells = this.applyInlineFormat(format, value);
      if (cells && Object.keys(cells).length > 0) {
        this.handleUserChange({ cells });
        return true;
      }
      return false;
    });
  }

  changeCellContent = (
    { rowId, columnId }: { rowId: string; columnId: string },
    delta: Delta,
  ) => {
    const {
      value: { rows, columns, cells },
    } = this.store.getState() as {
      value: TableDocument;
    };
    const rowIndex = rows
      ? rows.findIndex(({ insert: { id } }) => id === rowId)
      : -1;
    const columnIndex = columns
      ? columns.findIndex(({ insert: { id } }) => id === columnId)
      : -1;
    if (rowIndex === -1 || columnIndex === -1) {
      return;
    }

    const identity = stringifyCellIdentity(rowIndex, columnIndex);
    const currentContent = cells?.[identity]?.content ?? [];
    const content = currentContent.length
      ? delta.ops
      : // An implicit "\n" is there for empty cells
        new Delta().insert('\n').compose(delta).ops;
    this.handleUserChange({ cells: { [identity]: { content } } });
  };

  deleteTable = () => {
    this.remove();
  };

  getSelection(): Selection | null {
    return this.tableRef.current?.selection ?? null;
  }

  getActiveCellIdentity(): string | null {
    const selection = this.getSelection();
    return selection?.type === SelectionType.Cell ? selection.identity : null;
  }

  getCellLastRange(identity: string): Range | null {
    const quill = this.getCellQuill(identity);
    return quill ? getRangeSaver(quill).getLast() : null;
  }

  getCellQuill(identity: string): Quill | null {
    const tbody = this.domNode.querySelector('tbody');

    // querySelector is depth-first, so we use '>' to avoid selecting nested tables.
    const cellQuillContainer = tbody?.querySelector(
      `:scope > tr > td[${CELL_IDENTITY_ATTRIBUTE}="${identity}"] .ql-container`,
    );
    const quill = cellQuillContainer && findQuillByNode(cellQuillContainer);
    return quill instanceof Quill ? quill : null;
  }

  async getOrCreateCellQuill(identity: string): Promise<Quill | null> {
    return new Promise((resolve) => {
      const quill = this.getCellQuill(identity);
      if (quill || !this.tableRef.current) {
        resolve(quill);
        return;
      }

      this.tableRef.current.forceRenderCellWithQuill(identity, () => {
        resolve(this.getCellQuill(identity));
      });
    });
  }

  activateSelection() {
    const { selection } = this.tableRef.current || {};
    if (selection?.type === SelectionType.Cell) {
      this.getCellQuill(selection.identity)?.activateSelection();
    }
  }

  getCellClientRects(cell: {
    identity: string;
    index: number;
    length: number;
  }): Bounds[] {
    const cellQuill = this.getCellQuill(cell.identity);
    if (!cellQuill) return [];
    return cellQuill.getClientRects(cell.index, cell.length).map((rect) =>
      translateBoundary(
        rect,
        cellQuill.container,
        // @ts-expect-error
        this.scroll.domNode.parentElement,
      ),
    );
  }

  getBounds(cell: {
    identity: string;
    index: number;
    length: number;
  }): Bounds | null {
    const cellQuill = this.getCellQuill(cell.identity);
    if (!cellQuill) return null;
    const bounds = cellQuill.getBounds(cell.index, cell.length);
    if (!bounds) return null;
    return translateBoundary(
      bounds,
      cellQuill.container,
      // @ts-expect-error
      this.scroll.domNode.parentElement,
    );
  }

  mapCellsInRange<T>(
    rangeSelection: RangeSelection,
    mapper: (content: Delta['ops']) => T,
  ): (T | null)[][] {
    const { rows, columns, cells = {} } = this.value()['table-embed'];
    const { rowIndexes, columnIndexes } = getRangeSelectionIndexes(
      rangeSelection,
      { rows, columns },
    );

    return rowIndexes.map((rowIndex) =>
      columnIndexes.map<T | null>((columnIndex) => {
        const cellContent =
          cells[stringifyCellIdentity(rowIndex, columnIndex)]?.content;

        return cellContent ? mapper(cellContent) : null;
      }),
    );
  }

  private getClipboardDataForRange(rangeSelection: RangeSelection) {
    const { rows, columns } = this.value()['table-embed'];
    const { columnIndexes } = getRangeSelectionIndexes(rangeSelection, {
      rows,
      columns,
    });

    const assetToken = getAssetTokenFromBlot(this);
    const cellsData = this.mapCellsInRange(rangeSelection, (cellContent) => ({
      html: deltaToHTML(cellContent, {
        assetToken,
        preferOmittingParagraphTag: true,
      }),
      text: deltaToText(cellContent),
    }));

    const getWidthAttribute = (columnIndex: number) => {
      const index = columnIndexes[columnIndex];
      if (typeof index === 'number') {
        const columnAttributes = (columns ?? [])[index]?.attributes ?? {};
        const { width } = columnAttributes;
        if (typeof width === 'number') {
          return ` width="${_.escape(String(width))}"`;
        }
      }
      return '';
    };

    return {
      html: `<table><tbody>${cellsData
        .map(
          (columnData, rowIndex) =>
            `<tr>${columnData
              .map(
                (cell, columnIndex) =>
                  `<td${rowIndex === 0 ? getWidthAttribute(columnIndex) : ''}>${
                    cell?.html ?? ''
                  }</td>`,
              )
              .join('')}</tr>`,
        )
        .join('')}</tbody></table>`,
      text: _.flatten(cellsData)
        .map((cell) => cell?.text)
        .join('\n'),
    };
  }

  handleUserChange(change: TableChange) {
    const newValue = tableHandler.compose(this.value()['table-embed'], change);
    this.setState({ value: { ...newValue } });

    // @ts-expect-error
    this.scroll.emitEmbedUpdate(this, change);
  }

  insertRowOrColumn = (at: number, isRow: boolean) => {
    const id = generateDeltaRandomId();
    const delta = new Delta().retain(at).insert({ id }).ops;
    const key = isRow ? 'rows' : 'columns';

    this.handleUserChange({ [key]: delta });
  };

  deleteRowOrColumn = (at: number, isRow: boolean) => {
    const { value } = this.store.getState() as {
      value: TableDocument;
    };

    const items = isRow ? value.rows : value.columns;
    if ((items?.length ?? 0) <= 1) {
      this.deleteTable();
    } else {
      const delta = new Delta().retain(at).delete(1).ops;
      const key = isRow ? 'rows' : 'columns';

      this.handleUserChange({ [key]: delta });
    }
  };

  rearrangeRowOrColumn = (from: number, to: number, isRow: boolean) => {
    if (from === to) return;

    const { value } = this.store.getState() as {
      value: TableDocument;
    };

    const cells: Record<string, TableDocumentCell> = {};
    Object.keys(value.cells || {}).forEach((identity) => {
      const [rowIndex, columnIndex] = parseCellIdentity(identity);
      const index = isRow ? rowIndex : columnIndex;
      if (index === from) {
        const cell = value.cells?.[identity];
        const newIdentity = isRow
          ? stringifyCellIdentity(to, columnIndex)
          : stringifyCellIdentity(rowIndex, to);
        if (cell) cells[newIdentity] = cell;
      }
    });

    const property = isRow ? 'rows' : 'columns';
    const ops = value[property];
    let existingAttributes: Record<string, unknown> | undefined;
    if (ops) {
      const delta = new Delta(ops).slice(from, from + 1);
      existingAttributes = delta.ops[0]?.attributes;
    }

    this.handleUserChange({
      cells,
      [property]: new Delta()
        .retain(from)
        .delete(1)
        .compose(
          new Delta()
            .retain(to)
            .insert({ id: generateDeltaRandomId() }, existingAttributes),
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ).ops as any,
    });
  };

  resizeColumn = (columnId: string, width: number) => {
    const table = this.value()['table-embed'];
    const columnIndex = (table.columns || []).findIndex(
      (column) => column.insert.id === columnId,
    );
    if (columnIndex === -1) return;

    const delta = new Delta().retain(columnIndex).retain(1, { width })
      .ops as TableChangeColumn[];
    this.handleUserChange({ columns: delta });
  };

  updateContent(change: TableChange) {
    const doc = this.value()['table-embed'];
    const newValue = tableHandler.compose(doc, change);

    // To ensure consistency of collaborative editing, we must update existing
    // cells synchronously instead of relying on React’s asynchronous render
    // hooks. Otherwise, the cell currently being edited may become corrupted.
    const invertedRows =
      change.rows && new Delta(change.rows).invert(new Delta(doc.rows));
    const invertedColumns =
      change.columns &&
      new Delta(change.columns).invert(new Delta(doc.columns));

    const changesToExistingCells =
      tableHandler.compose(
        { cells: change.cells },
        { rows: invertedRows?.ops, columns: invertedColumns?.ops },
      ).cells || {};

    Object.keys(changesToExistingCells).forEach((identity) => {
      const { content } = changesToExistingCells?.[identity] || {};
      if (content) {
        const quill = this.getCellQuill(identity);
        quill?.editReadOnly(() => {
          if (doc.cells?.[identity]?.content) {
            quill?.updateContents(new Delta(content), Quill.sources.API);
          } else {
            quill?.setContents(new Delta(content), Quill.sources.API);
          }
        });
      }
    });

    this.setState({ value: newValue });
  }

  value(): { 'table-embed': TableDocument } {
    // @ts-expect-error
    return { [this.statics.blotName]: this.store.getState().value };
  }

  async moveCaretIntoCellWithKeyboard(
    identity: string,
    movedInOptions: BlockEmbedMovedInOptions,
  ) {
    const quill = await this.getOrCreateCellQuill(identity);
    if (quill) {
      const { direction } = movedInOptions;
      const [line] = quill.getLine(
        direction === 'right' || direction === 'down'
          ? 0
          : quill.getLength() - 1,
      );

      if (line) {
        moveCaretIntoLineWithKeyboard(line, movedInOptions);
      }
    }
  }

  // @ts-expect-error
  render(state) {
    const {
      value: { cells, rows, columns },
      span,
      selected,
    } = state;

    return (
      <Table
        blot={this}
        cells={cells}
        rows={rows}
        selected={selected}
        columns={columns}
        span={span}
        tableRef={this.tableRef}
        onPaste={this.handlePaste}
        deleteTable={this.deleteTable}
        insertRowOrColumn={this.insertRowOrColumn}
        deleteRowOrColumn={this.deleteRowOrColumn}
        changeCellContent={this.changeCellContent}
        resizeColumn={this.resizeColumn}
        rearrangeRowOrColumn={this.rearrangeRowOrColumn}
        isEmbedded={this.isEmbedded}
      />
    );
  }
}

TableEmbed.blotName = 'table-embed';
TableEmbed.className = 'ql-table-embed';
TableEmbed.tagName = 'div';

function getDeltaForApplyingInlineFormat(
  delta: Delta,
  format: string,
  value: true | null,
) {
  const result = new Delta();
  delta.eachLine((line, attributes) => {
    line.forEach((op) => {
      const length = Op.length(op);
      if (
        typeof op.insert !== 'string' ||
        op.attributes?.[format] === value ||
        (!op.attributes?.[format] && !value)
      ) {
        result.retain(length);
        return;
      }

      if (!(attributes.header && format === 'bold' && value)) {
        result.retain(length, { [format]: value });
      } else {
        result.retain(length);
      }
    });

    result.retain(1);
  });

  return result.chop();
}

export default TableEmbed;
